import {useExternalAnchor, type PropsWithPartialAnchor} from '@github-ui/react-core/react-partial-anchor'
import type {Repository} from '@github-ui/current-repository'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {useEffect, useState, useCallback} from 'react'
import {AnchoredOverlay} from '@primer/react'
import {CurrentRepositoryProvider} from '@github-ui/current-repository'
import {GlobalCopilotOverlay} from './GlobalCopilotOverlay'
import {CCAPopover} from './CCAPopover'
import {verifiedFetch} from '@github-ui/verified-fetch'

import {ConsecutiveCAPI401sProvider} from '@github-ui/agent-sessions/contexts/ConsecutiveCAPI401sContext'
import {useQueryClient} from '@github-ui/react-query'
import {agentTasksQueryKey, fetchTasks, TASKS_STORAGE_KEY} from '@github-ui/agent-sessions/hooks/useTasks'
import {
  fetchPullRequests,
  PR_MAP_STORAGE_KEY,
  getSortedResourceIds,
} from '@github-ui/agent-sessions/hooks/usePullRequests'
import {useLocalStorageCache} from '@github-ui/agent-sessions/hooks/useLocalStorageCache'
import type {BaseTask} from '@github-ui/agent-sessions/types/task'
import type {IdToPullRequestMap} from '@github-ui/agent-sessions/types/pull'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useCopilotChatPayload} from '@github-ui/global-copilot-menu/hooks/useCopilotChatPayload'

// Custom event name for toggling the mission control overlay
export const TOGGLE_COPILOT_OVERLAY = 'toggle-copilot-overlay'

export function GlobalAgentsMenu(
  props: PropsWithPartialAnchor<{
    repository: Repository
    popoverAvailable?: boolean
    agentsButtonId?: string
    apiURL?: string | null
  }>,
) {
  const {setValue: setCachedTasks} = useLocalStorageCache<BaseTask[]>(TASKS_STORAGE_KEY)
  const {setValue: setCachedPRMap} = useLocalStorageCache<IdToPullRequestMap>(PR_MAP_STORAGE_KEY)
  const copilotChatPayload = useCopilotChatPayload()
  const queryClient = useQueryClient()
  const isTaskPrefetchingEnabled = isFeatureEnabled('copilot_agent_prefetch_tasks')
  const [hasPrefetched, setHasPrefetched] = useState(false)
  const {ref: anchorRef, open, setOpen} = useExternalAnchor(props.reactPartialAnchor)
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const {repository, apiURL, popoverAvailable, agentsButtonId} = props
  const handleClose = () => {
    setOpen(false)
  }

  const [showPopover, setShowPopover] = useState(popoverAvailable || false)
  const dismissCCAPopover = useCallback(async () => {
    setShowPopover(false)
    await verifiedFetch('/settings/dismiss-notice/copilot_coding_agent_entry_point_popover', {method: 'POST'})
  }, [setShowPopover])

  useEffect(() => {
    if (open && showPopover) {
      dismissCCAPopover()
    }
  }, [open, dismissCCAPopover, showPopover])

  useEffect(() => {
    if (open) {
      sendClickAnalyticsEvent({
        category: 'copilot-coding-agent-global-dropdown',
        action: 'open',
      })
    }
  }, [open, sendClickAnalyticsEvent])

  // Listen for the custom keyboard event to open the copilot overlay
  useEffect(() => {
    const handleToggleCopilotOverlay = () => {
      // Until Primer React Tooltip bug is fixed, temporarily hide zombie tooltip when opening overlay to prevent dual tooltips
      // https://github.com/github/primer/issues/5518
      const tooltip = anchorRef.current?.nextElementSibling
      if (!open && tooltip && tooltip instanceof HTMLElement && tooltip.matches('tool-tip[popover="manual"]')) {
        tooltip.setAttribute('style', 'display: none')
      }
      setOpen(!open)
    }

    document.addEventListener(TOGGLE_COPILOT_OVERLAY, handleToggleCopilotOverlay)
    return () => {
      document.removeEventListener(TOGGLE_COPILOT_OVERLAY, handleToggleCopilotOverlay)
    }
  }, [setOpen, open, anchorRef])

  // Listen for button hover to prefetch tasks and pull requests
  useEffect(() => {
    // Don't add listener if already prefetched
    if (hasPrefetched || !isTaskPrefetchingEnabled) return

    const prefetchTasksAndPullRequests = async () => {
      try {
        // First prefetch tasks
        await queryClient.prefetchQuery({
          // eslint-disable-next-line @tanstack/query/exhaustive-deps
          queryKey: agentTasksQueryKey,
          queryFn: async () => await fetchTasks(queryClient, setCachedTasks, apiURL ?? copilotChatPayload?.apiURL),
        })

        // Get the cached tasks to prefetch pull requests
        const cachedTasks = queryClient.getQueryData<BaseTask[]>(agentTasksQueryKey)
        if (cachedTasks && cachedTasks.length > 0) {
          const tasksWithResourceIds = cachedTasks.filter(task => task.resource_id !== undefined)
          if (tasksWithResourceIds.length > 0) {
            const pullRequestIds = getSortedResourceIds(tasksWithResourceIds)
            await queryClient.prefetchQuery({
              // eslint-disable-next-line @tanstack/query/exhaustive-deps
              queryKey: ['agent-sessions-pull-requests', pullRequestIds],
              queryFn: async () => await fetchPullRequests(tasksWithResourceIds, setCachedPRMap),
            })
          }
        }

        setHasPrefetched(true)
      } catch {
        // Silently handle prefetch errors so they don't block the UI
      }
    }

    const buttonElement = document.getElementById(agentsButtonId ?? 'global-copilot-agent-button')
    if (!buttonElement) return

    buttonElement.addEventListener('mouseenter', prefetchTasksAndPullRequests)

    return () => {
      buttonElement.removeEventListener('mouseenter', prefetchTasksAndPullRequests)
    }
  }, [
    hasPrefetched,
    queryClient,
    setCachedTasks,
    setCachedPRMap,
    apiURL,
    copilotChatPayload?.apiURL,
    isTaskPrefetchingEnabled,
    agentsButtonId,
  ])

  // Until Primer React Tooltip bug is fixed, restores the tooltip functionality
  // https://github.com/github/primer/issues/5518
  useEffect(() => {
    if (!open) {
      const tooltip = anchorRef.current?.nextElementSibling
      if (tooltip && tooltip instanceof HTMLElement && tooltip.matches('tool-tip[popover="manual"]')) {
        tooltip.removeAttribute('style')
      }
    }
  }, [open, anchorRef])

  return (
    <ConsecutiveCAPI401sProvider>
      <CCAPopover showPopover={showPopover} dismissPopover={dismissCCAPopover} />
      <AnchoredOverlay
        width="large"
        variant={{regular: 'anchored', narrow: 'fullscreen'}}
        align="end"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={handleClose}
        anchorRef={anchorRef}
        renderAnchor={null}
        preventOverflow={false}
        focusZoneSettings={{disabled: true}}
      >
        <CurrentRepositoryProvider repository={repository}>
          <GlobalCopilotOverlay apiURL={apiURL} onClose={handleClose} />
        </CurrentRepositoryProvider>
      </AnchoredOverlay>
    </ConsecutiveCAPI401sProvider>
  )
}

try{ GlobalAgentsMenu.displayName ||= 'GlobalAgentsMenu' } catch {}